import React, { useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './article.css';
import Amalgam from '../../assets/images/Amalgam-logo.png';
import LinkedIn from '../../assets/images/LinkedIn.png';
import Breadcrumb from '../../assets/components/BreadCrumb';
import { useMediaQuery } from 'react-responsive';
import Arrow from '../../assets/images/Arrow1.png';
import { useNavigate } from 'react-router-dom';

function PFA() {
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const sectionRef = useRef<HTMLElement>(null);
    const isMobile = useMediaQuery({ query: '(max-width: 768px)' });

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [pathname]);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    const sections = entry.target.getElementsByClassName('content-section');
                    Array.from(sections).forEach((section) => {
                        if (entry.isIntersecting) {
                            section.classList.add('animate');
                        } else {
                            section.classList.remove('animate');
                        }
                    });
                });
            },
            {
                threshold: 0.2
            }
        );

        if (sectionRef.current) {
            observer.observe(sectionRef.current);
        }

        return () => {
            if (sectionRef.current) {
                observer.unobserve(sectionRef.current);
            }
        };
    }, []);

    return (
        <div className='article-container' >
            <div>
                <Breadcrumb route="/case-studies" pageTitle="Premier Financial Alliance" />
            </div>
            <section className='article-row-1' id='pfa-background'>
                <p>Insurance</p>
                <h2>Premier Financial Alliance</h2>
                <div className="article-details fade-in-right">
                    <div className="article-detail-item">
                        <span>Location: Suwanee, Georgia 🇺🇲</span>
                    </div>
                </div>
            </section>


            <section className='article-intro-section'>
                <h1>How We Streamlined Premier Financial Alliance's Employee Operations</h1>
                <p>
                    Premier Financial Alliance (PFA) is an insurance brokerage company that's disrupting the industry with a proprietary financial product focusing on Middle America: life insurance you don't have to die to use. They offer whole life, universal life, and term life insurance, as well as annuities, and they strive to protect families and bring peace of mind to everyone they touch.
                </p>
            </section>

            <section className='article-row-2' ref={sectionRef}>
                <div className='content-section'>
                    <h3>Challenge</h3>
                    <p>Premier Financial Alliance struggled with outdated technology and an inefficient platform. As a result, they suffered from unnecessary manual processes and reduced efficiency for agent onboarding, reporting, and administrative tasks. They risked wasting resources, inaccurately distributing incentives, and making poor decisions.</p>
                </div>
                <div className='content-section'>
                    <h3>Approach</h3>
                    <p>We modernized their platform, creating automated processes to manage complex data relationships, optimizing agent onboarding, and improving PFA's reporting capabilities. We migrated their data, refined their business logic, enabled secure team communication, and improved administrative workflows.</p>
                </div>
                <div className='content-section'>
                    <h3>Results</h3>
                    <p>PFA can now count on a solid incentive management system, secure team communication, and dynamic custom reporting. This streamlines their operations, and empowers them to make better, data-driven decisions also when it comes to incentive distribution.</p>
                </div>
            </section>
            <section className='article-section-6'>
                <h2>The Challenge</h2>
                <h3>Outdated technology and processes can slow down daily operations for revenue-driving and for administrative employees.</h3>
                <p>
                    This is especially true in the insurance space, where complex logic and data relationships feed vital metrics like sales numbers, retention, and even commission payouts.
                    Premier Financial Alliance (PFA) relied on manual processes that lacked key business logic. They also struggled with the inflexibility of their reports and counterintuitive UI.
                    They lacked confidence in key data affecting personal incentives, and couldn't standardize administrative tasks that are essential for onboarding new hires.
                    They also wasted time whenever they had to create custom reports, which were needed to observe how the agents fared in terms of business generation and competed against one another, as well as to track revenue generation per agent.
                    Hadn't they found a solution to their issue, they would've run a number of risks:
                </p>
                <ul>
                    <li>Resource wastage caused by operational inefficiencies</li>
                    <li>Employee demotivation and disputes due to inaccurate or unfair incentive distribution</li>
                    <li>Employee dissatisfaction and higher turnover because of cumbersome processes, counterintuitive UI, increased workload, and stress</li>
                    <li>Poor decision-making due to inconsistent and unorganized data</li>
                    <li>Impeded ability to track performance metrics and generate actionable insights because of inflexible reporting</li>
                    <li>Difficulties growing caused by poor data management and non-standardized administrative tasks</li>
                </ul>
                <p>
                    PFA started looking for ways to empower and increase the efficiency of their insurance agents and administrative employees. The new solution they were looking for would make their task management much smoother all while maintaining the integrity of the detailed data relationships powering incentives, rewards, and commissions. And that's exactly what we at Amalgam helped them achieve.
                </p>
            </section>

            <section className='article-section-3'>

                <h2>The Solution</h2>
                <h3>Our Approach:</h3>
                <p>
                    The first step in our approach was an application assessment to evaluate the structure and determine the current level of maturity of PFA's web application architecture. This helped us design an improvement plan.
                </p>
                <p>
                    To preserve the relationships between PFA's data, we used Java to design the necessary data access objects, and incorporated fields for personal agents' reports.
                    We then created new processes and incremental business logic to optimize the agent onboarding and team communication.
                    We transferred legacy data from mySQL to PostgreSQL, scrubbed it, and added new fields that would facilitate the management of PFA's insurance agents.
                    Finally, working together with our client's management, we engineered a new interface that would facilitate personal reporting for all agents thanks to its modern features and intuitive design.
                </p>

                <h3>Our Step-by-Step Process:</h3>
                <ul>
                    <li>An initial application architecture and code assessment</li>
                    <li>The creation of new reporting logic and key data fields</li>
                    <li>The creation of a new administrative logic</li>
                    <li>Data migration and clean-up</li>
                    <li>The revamp of PFA's UX/UI interfaces</li>
                </ul>

                <h3>The Results</h3>
                <ul>
                    <li>We enhanced our client's technical capabilities. Now, it's much easier for their administrators to create and update agent hierarchy profiles: their legacy platform relied on a manual process for creating and maintaining complex relationships dictating commission payouts. We designed an automated process that creates these relationships and an interface that provides the admins the ability to edit data relationships all while maintaining their integrity.</li>
                    <li>We made it easy for PFA's admins to edit their agents' profile data. Their legacy administrative platform didn't allow administrators to easily view and edit critical aspects of agent data. We created business logic wrapped in a contemporary UX/UI to solve this issue.</li>
                    <li>We enabled administrators to push and encrypt essential team communications. Our client's legacy email server lacked key security and backup protocols that translated into key communications being lost. That's why we migrated the client to a contemporary email server.</li>
                    <li>We empowered PFA's agents to create dynamic personal reports. Our client's admins were unable to create custom personal reports to review the performance of individual agents. We created new reporting logic wrapped in a contemporary UX/UI to overcome this issue.</li>
                </ul>
            </section>

            <section className='article-section-4'>
                <h2>Conclusions</h2>

                <h3>Summary of Benefits</h3>
                <p>
                    We successfully revamped our client's data structures, created a new reporting logic, and refined their UI, thus optimizing PFA's incentives engine and empowering their leaders to manage their agents seamlessly and effectively.
                </p>

                <h3>Contact Information</h3>
                <p>
                    Are you ready to leverage cutting-edge technology for your organization's needs?
                    Contact us at <a href="mailto:hello@amalgam-inc.com">hello@amalgam-inc.com</a>.
                </p>
            </section>


            <section className='ourwork-row-14 blog-row-14'>

                <h2>Get in touch</h2>
                <p>Reach out to us to gather more information or <br className='blog-row-14-break' />  set our wheels in motion.</p>
                <div className='ourwork-arrow-box blog-arrow-box'>
                    <img src={Arrow} alt="arrow" className='ourwork-arrow' />
                    <button onClick={() => navigate("/contactus")}>Let's talk</button>
                </div>

            </section>

            {!isMobile ? (
                <section className='home-footer'>
                    <div className='home-footer-middle'>
                        <img src={Amalgam} alt="Amalgam" className='home-footer-amalgam' />
                        <Link to="/" className='home-footer-links'>Home</Link>
                        <Link to="/ourwork" className='home-footer-links'>Our Work</Link>
                        <Link to="/aboutus" className='home-footer-links'>About Us</Link>
                        <Link to="/case-studies" className='home-footer-links'>Case Studies</Link>
                        <Link to="/startups" className='home-footer-links'>Launchpad</Link>
                        <Link to="/careers" className='home-footer-links'>Careers</Link>
                        <Link to="/contactus" className='home-footer-links'>Contact Us</Link>
                        <img src={LinkedIn} alt="Instagram" className='home-footer-linkedin' />
                    </div>
                    <div className='home-footer-dividor'>
                        <div className='home-footer-policies'>
                            <p><Link to="/privacy-policy">Privacy Policy</Link></p>
                            <p><Link to="/terms-and-conditions">Terms and Conditions</Link></p>
                            <p><Link to="/cookie-policy">Cookie Policy</Link></p>
                        </div>
                    </div>
                </section>
            ) : (
                <section>
                    <div className="footer-minimal">
                        <div className="footer-content">
                            {/* <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '20px' }}>
                <img src={Amalgam} alt="Amalgam" className="footer-logo" />
                <img src={LinkedIn} alt="Instagram" className='home-footer-linkedin' style={{ alignSelf: "center" }} />
              </div> */}
                            <div className="footer-top-row">
                                <Link to="/" className="footer-link">Home</Link>
                                <span className="footer-separator">•</span>
                                <Link to="/ourwork" className="footer-link">Our Work</Link>
                                <span className="footer-separator">•</span>
                                <Link to="/aboutus" className="footer-link">About Us</Link>
                                <span className="footer-separator">•</span>
                                <Link to="/case-studies" className="footer-link">Case Studies</Link>
                            </div>
                            <div className="footer-bottom-row">
                                <Link to="/startups" className="footer-link">Launchpad</Link>
                                <span className="footer-separator">•</span>
                                <Link to="/careers" className="footer-link">Careers</Link>
                                <span className="footer-separator">•</span>
                                <Link to="/contactus" className="footer-link">Contact</Link>
                            </div>
                        </div>
                    </div>

                    <div style={{
                        width: "80%",
                        height: "0px",
                        borderTop: "1px solid black",
                        backgroundColor: "white",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        paddingBottom: "20px",
                        margin: "0 auto"
                    }}>
                    </div>
                    <div style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        backgroundColor: "white",
                        gap: isMobile ? "5px" : "20px",
                        paddingBottom: "20px",
                        width: "100%",
                        padding: "0 10px 20px 10px",
                        textAlign: "center"
                    }}>
                        <Link to="/privacy-policy" style={{ textAlign: "center", color: "black", textDecoration: "none" }}>Privacy Policy</Link>
                        <span style={{ color: "black", fontSize: "14px" }}>|</span>
                        <Link to="/terms-and-conditions" style={{ color: "black", textDecoration: "none" }}>Terms and Conditions</Link>
                        <span style={{ color: "black", fontSize: "14px" }}>|</span>
                        <Link to="/cookie-policy" style={{ fontSize: "14px", color: "black", textDecoration: "none" }}>Cookie Policy</Link>
                    </div>
                </section>
            )}
        </div>
    );
}

export default PFA;